// components/AudioRecorderProvider.tsx
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useReactMediaRecorder } from 'react-media-recorder';

import {
  availableAudioDevicesAtom,
  mediaBlobUrlAtom,
  monitoringStreamAtom,
  pauseRecordingAtom,
  previewAudioStreamAtom,
  recorderStatusAtom,
  selectedAudioDeviceIdAtom,
  startRecordingAtom,
  stopRecordingAtom,
} from '../../atoms';

/** Stops all active tracks in a MediaStream */
const stopStreamTracks = (stream: MediaStream | null) => {
  if (!stream) return;
  stream.getTracks().forEach((track) => {
    if (track.readyState !== 'ended') {
      track.stop();
    }
  });
};

const AudioRecorderProvider = ({ children }: { children: ReactNode }) => {
  const selectedDeviceId = useAtomValue(selectedAudioDeviceIdAtom);
  const availableDevices = useAtomValue(availableAudioDevicesAtom);

  // Build audio constraints based on selected device
  const audioConstraints = useMemo(() => {
    if (!selectedDeviceId) {
      return true; // Use default device
    }

    // Check if selected device is still available
    const hasDevice = availableDevices.some(
      (device) => device.deviceId === selectedDeviceId,
    );
    if (!hasDevice) {
      return true; // Fall back to default if device no longer exists
    }

    return {
      deviceId: { exact: selectedDeviceId },
    };
  }, [selectedDeviceId, availableDevices]);

  const {
    status,
    mediaBlobUrl,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    clearBlobUrl,
    previewAudioStream,
  } = useReactMediaRecorder({
    audio: audioConstraints,
    video: false,
    blobPropertyBag: { type: 'audio/webm' },
  });

  const setStatus = useSetAtom(recorderStatusAtom);
  const setBlobUrl = useSetAtom(mediaBlobUrlAtom);
  const setPreviewAudioStream = useSetAtom(previewAudioStreamAtom);
  const handleStartRecording = useSetAtom(startRecordingAtom);
  const handlePauseRecording = useSetAtom(pauseRecordingAtom);
  const handleStopRecording = useSetAtom(stopRecordingAtom);

  const [monitoringStream, setMonitoringStream] = useAtom(monitoringStreamAtom);

  const [isCancelPending, setIsCancelPending] = useState(false);

  // Sync recorder status to atoms and handle stream cleanup
  useEffect(() => {
    setStatus(status);
    if (status === 'recording') {
      handleStartRecording();
    } else if (status === 'paused') {
      handlePauseRecording();
    } else if (status === 'stopped') {
      // react-media-recorder stops the MediaRecorder but may not stop the MediaStream
      stopStreamTracks(previewAudioStream);
      handleStopRecording();
    }
  }, [
    status,
    setStatus,
    handleStartRecording,
    handlePauseRecording,
    handleStopRecording,
    previewAudioStream,
  ]);

  // Sync mediaBlobUrl to atom, with special handling for cancel flow
  useEffect(() => {
    if (isCancelPending && mediaBlobUrl) {
      clearBlobUrl();
      setBlobUrl(undefined);
      setIsCancelPending(false);
    } else {
      setBlobUrl(mediaBlobUrl);
    }
  }, [mediaBlobUrl, isCancelPending, clearBlobUrl, setBlobUrl]);

  // Sync previewAudioStream to atom
  useEffect(() => {
    if (previewAudioStream?.active) {
      setPreviewAudioStream(previewAudioStream);
    } else {
      setPreviewAudioStream(null);
    }
  }, [previewAudioStream, setPreviewAudioStream]);

  // Clear function for explicit cleanup (used by cancel flow)
  const clear = useCallback(() => {
    setIsCancelPending(true);
    stopRecording();
    stopStreamTracks(previewAudioStream);
    stopStreamTracks(monitoringStream);
    setPreviewAudioStream(null);
    setMonitoringStream(null);
  }, [
    stopRecording,
    previewAudioStream,
    monitoringStream,
    setPreviewAudioStream,
    setMonitoringStream,
  ]);

  // Expose control handlers via window for external access
  useEffect(() => {
    (window as any).__audioRecorder = {
      start: startRecording,
      stop: stopRecording,
      pause: pauseRecording,
      resume: resumeRecording,
      clear,
    };
  }, [startRecording, stopRecording, pauseRecording, resumeRecording, clear]);

  return <>{children}</>;
};

export default AudioRecorderProvider;
